import React, { useState, useMemo, useCallback } from "react";
import { TableVirtuoso } from "react-virtuoso";
import { Icon, Tooltip, Dropdown, Menu, Spin } from "antd";
import moment from "moment";
import { toBRL } from "~/components/UI/InputCurrency";
import { Status } from "~/components/UI";

var SOURCE_TAGS = {
    mensalidade: { label: 'Mensalidade', color: '#1890ff', bg: '#e6f7ff', border: '#91d5ff', icon: 'calendar' },
    extra:       { label: 'Extra',       color: '#722ed1', bg: '#f9f0ff', border: '#d3adf7', icon: 'star' },
    comissao:    { label: 'Comissão',    color: '#fa8c16', bg: '#fff7e6', border: '#ffd591', icon: 'percentage' },
    despesa:     { label: 'Despesa',     color: '#f5222d', bg: '#fff1f0', border: '#ffa39e', icon: 'arrow-up' },
    receita:     { label: 'Receita',     color: '#13c2c2', bg: '#e6fffb', border: '#87e8de', icon: 'arrow-down' },
};

var COL = {
    date: 120, status: 140, description: 0, source: 150, amount: 150, balance: 150,
};
var TABLE_W = 120 + 140 + 420 + 150 + 150 + 150;

var thBase = {
    padding: '10px 12px', background: '#fafafa',
    borderTop: '1px solid #e8e8e8', borderBottom: '1px solid #e8e8e8',
    borderRight: '1px solid #e8e8e8',
    fontWeight: 500, textAlign: 'left', whiteSpace: 'nowrap',
    overflow: 'hidden', textOverflow: 'ellipsis', fontSize: 13, color: '#595959',
};
var tdBase = {
    padding: '8px 12px', background: '#fff',
    borderBottom: '1px solid #f0f0f0', borderRight: '1px solid #f0f0f0',
    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 13,
};

function thS(w, extra) { return Object.assign({}, thBase, w ? { width: w } : {}, extra || {}); }
function tdS(w, extra) { return Object.assign({}, tdBase, w ? { width: w } : {}, extra || {}); }

var SORTERS = {
    date:    function(a, b) { return (a.date || '').localeCompare(b.date || ''); },
    amount:  function(a, b) { return (a.amount || 0) - (b.amount || 0); },
};

var TableList = React.memo(function TableList(props) {
    var data = props.data || [];
    var isLoading = props.isLoading;
    var summary = props.summary || {};
    var filter = props.filter || {};

    var [sortField, setSortField] = useState('date');
    var [sortOrder, setSortOrder] = useState('asc');
    var [statusFilter, setStatusFilter] = useState(null);
    var [sourceFilter, setSourceFilter] = useState(null);

    var processedData = useMemo(function() {
        var result = data;
        if (statusFilter) {
            result = result.filter(function(r) { return r.status === statusFilter; });
        }
        if (sourceFilter) {
            result = result.filter(function(r) { return r.source === sourceFilter; });
        }
        if (sortField && SORTERS[sortField]) {
            result = result.slice().sort(function(a, b) {
                var cmp = SORTERS[sortField](a, b);
                return sortOrder === 'asc' ? cmp : -cmp;
            });
        }
        return result;
    }, [data, statusFilter, sourceFilter, sortField, sortOrder]);

    var toggleSort = useCallback(function(field) {
        if (sortField === field) {
            setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortOrder('asc');
        }
    }, [sortField, sortOrder]);

    var sortIcon = function(field) {
        if (sortField !== field) return <Icon type="swap" style={{ marginLeft: 4, color: '#bfbfbf', fontSize: 11, transform: 'rotate(90deg)' }} />;
        return <Icon type={sortOrder === 'asc' ? 'arrow-up' : 'arrow-down'} style={{ marginLeft: 4, color: '#1890ff', fontSize: 11 }} />;
    };

    var tableComponents = useMemo(function() {
        return {
            Table: function(p) {
                return <table {...p} style={Object.assign({}, p.style, {
                    borderCollapse: 'separate', borderSpacing: 0, tableLayout: 'fixed', width: '100%', minWidth: TABLE_W,
                })} />;
            },
        };
    }, []);

    var fixedHeaderContent = useCallback(function() {
        return (
            <tr>
                <th style={thS(COL.date, { cursor: 'pointer' })} onClick={function() { toggleSort('date'); }}>
                    Data {sortIcon('date')}
                </th>
                <th style={thS(COL.status, { textAlign: 'center' })}>
                    Situação
                    <Dropdown trigger={['click']} overlay={
                        <Menu onClick={function(e) { setStatusFilter(e.key === 'all' ? null : e.key); }}>
                            <Menu.Item key="all">Todos</Menu.Item>
                            <Menu.Divider />
                            <Menu.Item key="Em Aberto">Em Aberto</Menu.Item>
                            <Menu.Item key="Pago">Pago</Menu.Item>
                        </Menu>
                    }>
                        <Icon type="filter" style={{ marginLeft: 4, color: statusFilter ? '#1890ff' : '#bfbfbf', cursor: 'pointer' }} />
                    </Dropdown>
                </th>
                <th style={thS(null)}>
                    Resumo do lançamento{' '}
                    <Tooltip title="Descrição do lançamento com a origem e categoria">
                        <Icon type="question-circle" style={{ color: '#bfbfbf' }} />
                    </Tooltip>
                </th>
                <th style={thS(COL.source)}>
                    Tipo
                    <Dropdown trigger={['click']} overlay={
                        <Menu onClick={function(e) { setSourceFilter(e.key === 'all' ? null : e.key); }}>
                            <Menu.Item key="all">Todos</Menu.Item>
                            <Menu.Divider />
                            {Object.keys(SOURCE_TAGS).map(function(k) {
                                return <Menu.Item key={k}>{SOURCE_TAGS[k].label}</Menu.Item>;
                            })}
                        </Menu>
                    }>
                        <Icon type="filter" style={{ marginLeft: 4, color: sourceFilter ? '#1890ff' : '#bfbfbf', cursor: 'pointer' }} />
                    </Dropdown>
                </th>
                <th style={thS(COL.amount, { textAlign: 'right', cursor: 'pointer' })} onClick={function() { toggleSort('amount'); }}>
                    Valor (R$) {sortIcon('amount')}
                </th>
                <th style={thS(COL.balance, { textAlign: 'right' })}>
                    Saldo (R$){' '}
                    <Tooltip title="Saldo acumulado considerando o saldo inicial da conta bancária selecionada">
                        <Icon type="question-circle" style={{ color: '#bfbfbf' }} />
                    </Tooltip>
                </th>
            </tr>
        );
    }, [sortField, sortOrder, statusFilter, sourceFilter, toggleSort]);

    var itemContent = useCallback(function(index, record) {
        var st = SOURCE_TAGS[record.source] || { label: record.source, color: '#8c8c8c', bg: '#fafafa', border: '#e8e8e8', icon: 'tag' };
        var isDebit = record.type === 'debit';
        return (
            <>
                <td style={tdS(COL.date, { textAlign: 'center' })}>
                    {record.date ? moment(record.date).format('DD/MM/YYYY') : '-'}
                </td>
                <td style={tdS(COL.status, { textAlign: 'center' })}>
                    <Status value={record.status || 'Em Aberto'} />
                </td>
                <td style={tdS(null, { color: '#262626' })}>
                    {record.description || '-'}
                </td>
                <td style={tdS(COL.source)}>
                    <span style={{
                        display: 'inline-flex', alignItems: 'center', gap: 5,
                        padding: '2px 8px 2px 6px', borderRadius: 4,
                        background: st.bg, border: '1px solid ' + st.border,
                        color: st.color, fontSize: 10, fontWeight: 600,
                        lineHeight: '18px', textTransform: 'uppercase',
                        letterSpacing: '0.03em', whiteSpace: 'nowrap',
                    }}>
                        <Icon type={st.icon} style={{ fontSize: 10 }} />
                        {st.label}
                    </span>
                </td>
                <td style={tdS(COL.amount, { textAlign: 'right', fontWeight: 600, color: isDebit ? '#f5222d' : '#262626' })}>
                    {(isDebit ? '-' : '') + toBRL(record.amount || 0)}
                </td>
                <td style={tdS(COL.balance, { textAlign: 'right', fontWeight: 500, color: (record.balance || 0) >= 0 ? '#262626' : '#f5222d' })}>
                    {toBRL(record.balance || 0)}
                </td>
            </>
        );
    }, []);

    return (
        <div className="table-clabs">
            <Spin spinning={!!isLoading}>
                <TableVirtuoso
                    style={{ height: 'calc(100vh - 320px)', border: '1px solid #e8e8e8', borderRadius: 4, background: '#fff' }}
                    data={processedData}
                    components={tableComponents}
                    fixedHeaderContent={fixedHeaderContent}
                    itemContent={itemContent}
                    overscan={20}
                />
                <div style={{ padding: '12px 16px', background: '#fafafa', border: '1px solid #e8e8e8', borderTop: 'none', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottomLeftRadius: 4, borderBottomRightRadius: 4 }}>
                    <div>
                        <strong>{processedData.length}</strong> lançamento{processedData.length !== 1 ? 's' : ''}
                        <div style={{ fontSize: 12, color: '#8c8c8c' }}>
                            {(filter.from ? moment(filter.from).format('DD/MM/YYYY') : '')} a {(filter.to ? moment(filter.to).format('DD/MM/YYYY') : '')}
                        </div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                        <div style={{ fontSize: 12, color: '#8c8c8c' }}>Valor total do período (R$)</div>
                        <div style={{ fontSize: 18, fontWeight: 700, color: summary.total_periodo >= 0 ? '#52c41a' : '#f5222d' }}>
                            {(summary.total_periodo < 0 ? '-' : '') + 'R$ ' + toBRL(Math.abs(summary.total_periodo || 0))}
                        </div>
                    </div>
                </div>
            </Spin>
        </div>
    );
});

export default TableList;
